import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Download, TrendingUp, Target, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PricesLab = () => {
  const [pct, setPct] = useState(7);
  const [inputPct, setInputPct] = useState("7");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchPreview = useCallback(async (currentPct) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/prices-preview?pct=${currentPct}`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error("Error cargando previsualización");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreview(pct);
  }, [pct, fetchPreview]);

  const handleInputChange = (v) => {
    setInputPct(v);
    const num = parseFloat(v);
    if (!isNaN(num) && num >= -20 && num <= 50) {
      setPct(num);
    }
  };

  const handleSliderChange = (v) => {
    setPct(v[0]);
    setInputPct(String(v[0]));
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/reports/prices-7pct/pdf?pct=${pct}`, {
        headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Tarifas_+${pct}pct_PortSitges_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF Tarifas +${pct}% descargado`);
    } catch (e) {
      toast.error("Error descargando PDF");
    } finally {
      setDownloading(false);
    }
  };

  const quickPresets = [-5, 0, 3, 5, 7, 10, 15, 20];

  const pctLabel = pct >= 0 ? `+${pct}%` : `${pct}%`;
  const pctColor = pct >= 15 ? "text-red-600" : pct >= 7 ? "text-amber-600" : pct >= 3 ? "text-emerald-600" : "text-slate-600";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl mx-auto" data-testid="prices-lab-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
            Laboratorio de Tarifas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ajusta el porcentaje de crecimiento sobre 2025 y ve los precios recalculados al instante.
          </p>
        </div>
        <Button
          data-testid="download-pdf-btn"
          onClick={downloadPdf}
          disabled={downloading}
          className="bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Descargar PDF
        </Button>
      </div>

      {/* Control panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-rose-600" />
            Ajustar % de crecimiento sobre 2025
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className={`text-5xl font-bold font-mono ${pctColor}`} data-testid="current-pct">
              {pctLabel}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="pct-input" className="text-sm font-semibold">Valor:</Label>
                <Input
                  id="pct-input"
                  data-testid="pct-input"
                  type="number"
                  step="0.5"
                  min="-20"
                  max="50"
                  value={inputPct}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="w-24 font-mono text-lg text-center"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
              <Slider
                data-testid="pct-slider"
                min={-20}
                max={50}
                step={1}
                value={[pct]}
                onValueChange={handleSliderChange}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>−20%</span>
                <span>0%</span>
                <span>+25%</span>
                <span>+50%</span>
              </div>
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <span className="text-xs font-semibold text-slate-500 mr-1 self-center">Presets:</span>
            {quickPresets.map((v) => (
              <Button
                key={v}
                data-testid={`preset-${v}`}
                size="sm"
                variant={v === pct ? "default" : "outline"}
                onClick={() => {
                  setPct(v);
                  setInputPct(String(v));
                }}
                className={v === pct ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
              >
                {v >= 0 ? `+${v}%` : `${v}%`}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Expected result */}
      {data && data.expected && (
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-lg">Resultado esperado con {pctLabel}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs opacity-80 uppercase tracking-wider">Objetivo revenue</div>
                <div className="text-2xl font-bold font-mono" data-testid="expected-target">
                  €{data.expected.target.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div>
                <div className="text-xs opacity-80 uppercase tracking-wider">Vs €1,9M</div>
                <div className={`text-2xl font-bold font-mono ${data.expected.gap_1900k >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {data.expected.gap_1900k >= 0 ? "+" : "−"}€{Math.abs(data.expected.gap_1900k).toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div>
                <div className="text-xs opacity-80 uppercase tracking-wider">Extra vs actual</div>
                <div className="text-2xl font-bold font-mono text-emerald-400">
                  +€{data.expected.extra_vs_current.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prices table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : data && data.rooms ? (
        <>
          {/* Alta Temporada */}
          <Card data-testid="high-season-table">
            <CardHeader className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-t-lg">
              <CardTitle className="text-lg">🔥 TEMPORADA ALTA — Jul · Ago · Sep</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-slate-100">Habitación</th>
                    <th className="p-2">#</th>
                    <th className="p-2">Jul L-J</th>
                    <th className="p-2">Jul V-S</th>
                    <th className="p-2">Ago L-J</th>
                    <th className="p-2">Ago V-S</th>
                    <th className="p-2">Sep L-J</th>
                    <th className="p-2">Sep V-S</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rooms.map((r, i) => (
                    <tr key={r.name} className={i % 2 ? "bg-red-50" : ""} data-testid={`row-${r.name.replace(/\s/g, "_")}-high`}>
                      <td className="p-2 font-semibold sticky left-0 bg-inherit">{r.name}</td>
                      <td className="text-center p-2 text-slate-500">{r.qty}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.jul_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-orange-600">€{r.prices.jul_vs}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.ago_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-orange-600">€{r.prices.ago_vs}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.sep_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-orange-600">€{r.prices.sep_vs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 text-xs text-slate-500 bg-slate-50 border-t">
                <Info className="w-3 h-3 inline mr-1" />
                <span>15 de Agosto (festivo): aplicar <b>+€15 extra</b> durante 5 noches (13-17 ago)</span>
              </div>
            </CardContent>
          </Card>

          {/* Temporada Baja */}
          <Card data-testid="low-season-table">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-t-lg">
              <CardTitle className="text-lg">🍂 TEMPORADA BAJA — Oct · Nov · Dic</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-slate-100">Habitación</th>
                    <th className="p-2">#</th>
                    <th className="p-2">Oct L-J</th>
                    <th className="p-2">Oct V-S</th>
                    <th className="p-2">Nov L-J</th>
                    <th className="p-2">Nov V-S</th>
                    <th className="p-2">Dic L-J</th>
                    <th className="p-2">Dic V-S</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rooms.map((r, i) => (
                    <tr key={r.name} className={i % 2 ? "bg-purple-50" : ""} data-testid={`row-${r.name.replace(/\s/g, "_")}-low`}>
                      <td className="p-2 font-semibold sticky left-0 bg-inherit">{r.name}</td>
                      <td className="text-center p-2 text-slate-500">{r.qty}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.oct_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-purple-600">€{r.prices.oct_vs}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.nov_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-purple-600">€{r.prices.nov_vs}</td>
                      <td className="text-center p-2 font-mono font-bold">€{r.prices.dic_lj}</td>
                      <td className="text-center p-2 font-mono font-bold text-purple-600">€{r.prices.dic_vs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 text-xs text-slate-500 bg-slate-50 border-t space-y-1">
                <div><Info className="w-3 h-3 inline mr-1" />Halloween/Castanyada (28-31 oct): <b>+€30</b></div>
                <div><Info className="w-3 h-3 inline mr-1" />Black Friday (27-30 nov): <b>+€40</b></div>
                <div><Info className="w-3 h-3 inline mr-1" />Puente Constitución (5-8 dic): <b>+€40</b></div>
              </div>
            </CardContent>
          </Card>

          {/* Festivos y Min/Max */}
          <Card data-testid="festive-table">
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
              <CardTitle className="text-lg">🎄 PICOS FESTIVOS + Min/Max</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-slate-100">Habitación</th>
                    <th className="p-2">#</th>
                    <th className="p-2">Navidad</th>
                    <th className="p-2">Fin de Año</th>
                    <th className="p-2 bg-slate-800 text-white">MIN</th>
                    <th className="p-2 bg-slate-800 text-white">MAX</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rooms.map((r, i) => (
                    <tr key={r.name} className={i % 2 ? "bg-emerald-50" : ""} data-testid={`row-${r.name.replace(/\s/g, "_")}-festive`}>
                      <td className="p-2 font-semibold sticky left-0 bg-inherit">{r.name}</td>
                      <td className="text-center p-2 text-slate-500">{r.qty}</td>
                      <td className="text-center p-2 font-mono font-bold text-emerald-700">€{r.prices.navidad}</td>
                      <td className="text-center p-2 font-mono font-bold text-emerald-700">€{r.prices["fin_año"]}</td>
                      <td className="text-center p-2 font-mono font-semibold text-slate-600">€{r.prices.min}</td>
                      <td className="text-center p-2 font-mono font-semibold text-slate-600">€{r.prices.max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 text-xs text-slate-500 bg-slate-50 border-t">
                <Info className="w-3 h-3 inline mr-1" />
                <span>Navidad: 24-26 dic · Fin de Año: 30 dic-2 ene · MIN/MAX configurar en Oscar → Tarifas → Precios mín/máx</span>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Explicación */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 text-sm text-blue-900 space-y-2">
          <p className="font-semibold flex items-center gap-2">
            <Info className="w-4 h-4" />
            ¿Cómo funciona?
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Los precios de la tabla son los <b>precios base</b> que debes meter en Oscar</li>
            <li>La RMA ajustará automáticamente <b>±30-45%</b> según ocupación y anticipación</li>
            <li>Los <b>Min/Max</b> son la red de seguridad: la RMA nunca traspasará esos límites</li>
            <li>El PDF descargable contiene toda esta información para imprimir y llevar a Oscar</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricesLab;
